/**
 * Per-user copy preferences. Stored in localStorage today; will move to the
 * `user_theme_preferences` table when the DB migration lands (sequencing step 4).
 *
 * Two independent toggles, both default true:
 *   - applyThemedCopy   — when false, themed labels are ignored and canonical
 *                         English is rendered. Color/asset theming still applies.
 *   - showRevealHints   — when false, the dotted underline + tooltip on themed
 *                         strings is suppressed (power users who've memorized
 *                         the mapping).
 */

export interface CopyPrefs {
  applyThemedCopy: boolean;
  showRevealHints: boolean;
}

export const DEFAULT_COPY_PREFS: CopyPrefs = {
  applyThemedCopy: true,
  showRevealHints: true,
};

const PREFS_KEY = "smplify_copy_prefs";

export function loadCopyPrefs(): CopyPrefs {
  if (typeof window === "undefined") return DEFAULT_COPY_PREFS;
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (!raw) return DEFAULT_COPY_PREFS;
    const parsed = JSON.parse(raw);
    return {
      applyThemedCopy:
        typeof parsed?.applyThemedCopy === "boolean" ? parsed.applyThemedCopy : true,
      showRevealHints:
        typeof parsed?.showRevealHints === "boolean" ? parsed.showRevealHints : true,
    };
  } catch {
    return DEFAULT_COPY_PREFS;
  }
}

export function saveCopyPrefs(prefs: CopyPrefs): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
  } catch {
    /* ignore quota / privacy mode */
  }
}
